import {
  type FaceTecSDKInstance,
  type FaceTecSessionRequestProcessor,
} from "facetec-sdk";

// Tracks the sessions started on an instance until they exit.
export type SessionTracker = {
  // Starts tracking a session; the returned processor reports its exit.
  track: (
    processor: FaceTecSessionRequestProcessor,
  ) => FaceTecSessionRequestProcessor;
  // Resolves once none of the tracked sessions is running.
  idle: () => Promise<void>;
};

export const createSessionTracker = (): SessionTracker => {
  let running = 0;
  let idle = Promise.resolve();
  let resolveIdle = () => {};

  const track = (
    processor: FaceTecSessionRequestProcessor,
  ): FaceTecSessionRequestProcessor => {
    if (running === 0) {
      idle = new Promise((resolve) => {
        resolveIdle = resolve;
      });
    }
    running += 1;

    // An exit is reported once per session.
    let exited = false;

    return {
      ...processor,
      onFaceTecExit: (result) => {
        if (!exited) {
          exited = true;
          running -= 1;
          if (running === 0) resolveIdle();
        }

        processor.onFaceTecExit(result);
      },
    };
  };

  return { track, idle: () => idle };
};

// The methods of the instance that start a session; a record, so a method
// added to the SDK fails the type check until it is listed here.
const startMethods: Record<keyof FaceTecSDKInstance, true> = {
  start3DLiveness: true,
  start3DLivenessThen3DFaceMatch: true,
  startIDScanOnly: true,
  startIDScanThen3D2DMatch: true,
  start3DLivenessThen3D2DPhotoIDMatch: true,
  startSecureOfficialIDPhotoCapture: true,
};

// Wraps the instance so that the sessions it starts are tracked.
export const trackInstanceSessions = (
  instance: FaceTecSDKInstance,
  tracker: SessionTracker,
): FaceTecSDKInstance => {
  const tracked = {} as FaceTecSDKInstance;

  for (const method of Object.keys(startMethods) as Array<
    keyof FaceTecSDKInstance
  >) {
    tracked[method] = (processor) => instance[method](tracker.track(processor));
  }

  return tracked;
};
